import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import type { MarkerSize } from 'static';
import { findMapDetails, mapIsAeternumMap } from 'static';
import type { MarkerRouteItem } from '../components/MarkerRoutes/MarkerRoutes';
import useMarkerRoute from '../components/MarkerRoutes/useMarkerRoute';
import { useMarkerSearchStore } from '../components/MarkerSearch/markerSearchStore';
import { latestLeafletMap } from '../components/WorldMap/useWorldMap';
import { fetchJSON } from '../utils/api';
import { getOverwolfFilters, useFiltersStore } from '../utils/filtersStore';
import { isOverwolfApp } from '../utils/overwolf';
import { useRealmStore } from '../utils/realmStore';
import { isEmbed, useRouteParams } from '../utils/routes';
import { usePersistentState } from '../utils/storage';
import { useUserStore } from '../utils/userStore';

export type MarkerBasic = {
  vitalsID?: string;
  catIDs?: string[];
  type: string;
  map?: string;
  realm?: string;
  position: [number, number, number];
  name?: string;
  chestType?: string;
  tier?: number;
  level?: number;
  levels?: number[];
  comments?: number;
  size?: MarkerSize;
  issues?: number;
  isTemporary?: boolean;
  customRespawnTimer?: number;
  _id: string;
  screenshotFilename?: string;
};

type MarkersContextProps = {
  markers: MarkerBasic[];
  setTemporaryHiddenMarkerIDs: (
    value: string[] | ((value: string[]) => string[]),
  ) => void;
  markerRoutes: MarkerRouteItem[];
  setMarkerRoutes: (routes: MarkerRouteItem[]) => void;
  toggleMarkerRoute: (markerRoute: MarkerRouteItem, force?: boolean) => void;
  visibleMarkers: MarkerBasic[];
  mode: Mode;
  setMode: React.Dispatch<React.SetStateAction<Mode>>;
};
const MarkersContext = createContext<MarkersContextProps>({
  markers: [],
  setTemporaryHiddenMarkerIDs: () => undefined,
  markerRoutes: [],
  setMarkerRoutes: () => undefined,
  toggleMarkerRoute: () => undefined,
  visibleMarkers: [],
  mode: null,
  setMode: () => undefined,
});

type MarkersProviderProps = {
  children: ReactNode;
};

type Mode = 'route' | 'marker' | null;

export function MarkersProvider({
  children,
}: MarkersProviderProps): JSX.Element {
  const { data: markers = [] } = useQuery(
    ['markers'],
    () =>
      Promise.all([
        fetchJSON<MarkerBasic[]>('/api/markers'),
        fetchJSON<MarkerBasic[]>('/api/auth/markers'),
      ]).then(([newMarkers, privateMarkers]) =>
        newMarkers.concat(privateMarkers),
      ),
    { refetchOnWindowFocus: false },
  );

  const [allMarkerRoutes, setMarkerRoutes] = usePersistentState<
    MarkerRouteItem[]
  >('markers-routes', []);
  const markerRoutes = isEmbed ? [] : allMarkerRoutes;

  const [mode, setMode] = useState<Mode>(null);
  const [temporaryHiddenMarkerIDs, setTemporaryHiddenMarkerIDs] = useState<
    string[]
  >([]);

  const { filters } = isOverwolfApp
    ? { filters: getOverwolfFilters() }
    : useFiltersStore();
  const { map, nodeId, routeId } = useRouteParams();
  const { data: markerRouteData } = useMarkerRoute(routeId);
  const hiddenMarkerIds = useUserStore(
    (state) => state.user?.hiddenMarkerIds || [],
  );
  const searchValues = useMarkerSearchStore((state) => state.searchValues);
  const markerFilters = useMarkerSearchStore((state) => state.markerFilters);
  const isPTR = useRealmStore((state) => state.isPTR);

  const visibleMarkers = useMemo(() => {
    const nameSearchValues = searchValues.filter((value) =>
      value.startsWith('name: '),
    );

    const isAeternumMap = mapIsAeternumMap(map);
    const mapDetails = findMapDetails(map);

    // Pre-compute route positions as a Set for O(1) lookup instead of O(n)
    const routePositions = new Set<string>();
    if (markerRouteData?.markerRoute.positions) {
      for (const pos of markerRouteData.markerRoute.positions) {
        routePositions.add(`${pos[0]},${pos[1]}`);
      }
    }
    for (const route of markerRoutes) {
      for (const pos of route.positions) {
        routePositions.add(`${pos[0]},${pos[1]}`);
      }
    }

    // Pre-compute filter sets for O(1) lookup
    const filtersSet = new Set(filters);
    const temporaryHiddenSet = new Set(temporaryHiddenMarkerIDs);
    const hiddenMarkerSet = new Set(hiddenMarkerIds);

    // Pre-compute search flags
    const hasCommentSearch = searchValues.includes('has: comment');
    const hasIssueSearch = searchValues.includes('has: issue');
    const isHiddenSearch = searchValues.includes('is: hidden');

    return markers.filter((marker) => {
      // Map check - early bailout
      if (marker.map) {
        if (mapDetails !== findMapDetails(marker.map)) {
          return false;
        }
      } else if (!isAeternumMap) {
        return false;
      }

      // Always show selected node
      if (marker._id === nodeId) {
        return true;
      }

      // Realm check - early bailout
      if (marker.realm) {
        if (isPTR && marker.realm !== 'ptr') {
          return false;
        }
        if (!isPTR && marker.realm !== 'live') {
          return false;
        }
      }

      // Route position check - O(1) lookup instead of nested loops
      const markerKey = `${marker.position[1]},${marker.position[0]}`;
      if (routePositions.has(markerKey)) {
        return true;
      }

      // Embed check
      if (isEmbed) {
        return !nodeId && !routeId;
      }

      // Marker filter check
      if (
        markerFilters.length > 0 &&
        !markerFilters.some((limit) => limit.markerIds.includes(marker._id))
      ) {
        return false;
      }

      // Filter check - moved earlier for better performance
      // Use Set for O(1) lookup instead of Array.some() which is O(n)
      let matchesFilter = false;
      if (marker.tier) {
        matchesFilter = filtersSet.has(`${marker.type}-${marker.tier}`);
      } else if (marker.size) {
        matchesFilter = filtersSet.has(`${marker.type}-${marker.size}`);
      } else {
        matchesFilter = filtersSet.has(marker.type);
      }

      if (!matchesFilter) {
        return false;
      }

      // Temporary hidden check - O(1) Set lookup
      if (temporaryHiddenSet.has(marker._id)) {
        return false;
      }

      // Comment search check
      if (hasCommentSearch && !marker.comments) {
        return false;
      }

      // Issue search check
      if (hasIssueSearch && !marker.issues) {
        return false;
      }

      // Hidden marker check - O(1) Set lookup
      if (
        (!isHiddenSearch && hiddenMarkerSet.has(marker._id)) ||
        (isHiddenSearch && !hiddenMarkerSet.has(marker._id))
      ) {
        return false;
      }

      // Name search check
      if (
        nameSearchValues.length > 0 &&
        !nameSearchValues.some((value) => value.slice(6) === marker.name)
      ) {
        return false;
      }

      return true;
    });
  }, [
    filters,
    markers,
    hiddenMarkerIds,
    temporaryHiddenMarkerIDs,
    map,
    markerFilters,
    searchValues,
    nodeId,
    markerRoutes,
    markerRouteData,
    isPTR,
    routeId,
  ]);

  const toggleMarkerRoute = (markerRoute: MarkerRouteItem, force?: boolean) => {
    const markerRoutesClone = [...markerRoutes];
    const index = markerRoutesClone.findIndex(
      (targetMarkerRoute) => targetMarkerRoute._id === markerRoute._id,
    );
    if (index > -1 && force !== true) {
      markerRoutesClone.splice(index, 1);
    } else if (force !== false) {
      markerRoutesClone.push(markerRoute);
      if (latestLeafletMap) {
        latestLeafletMap.fitBounds(markerRoute.positions);
      }
    }

    setMarkerRoutes(markerRoutesClone);
  };

  return (
    <MarkersContext.Provider
      value={{
        markers,
        setTemporaryHiddenMarkerIDs,
        visibleMarkers,
        markerRoutes,
        setMarkerRoutes,
        toggleMarkerRoute,
        mode,
        setMode,
      }}
    >
      {children}
    </MarkersContext.Provider>
  );
}

export function useMarkers(): MarkersContextProps {
  return useContext(MarkersContext);
}
